/**
 * EO «Конфигуратор → Филиалы» — конфиг-карточка филиала по эталону
 * design-references/screen-16-eo-config-branches.html + screen-16-config.js.
 *
 * BranchTabs (без чипа «ВСЕ») → 2-колоночная конфиг-сетка (Тип сетки /
 * Режим работы / Отсечка чайных / «Чайная до {cutoff}» с живым лейблом) →
 * ночной ряд из 3 (Начало/Конец — TimePicker step 15; Надбавка — NumPad
 * money) + helper 9px с живой подстановкой границ окна → список чайных.
 *
 * NULL night_cutoff/night_end → DEFAULT_NIGHT_CUTOFF/DEFAULT_NIGHT_END из
 * @ub/shared (дефолт объявлен один раз, не хардкод). «Чайной после» в
 * утверждённом эталоне НЕТ — не добавлять. Смена филиала при dirty (открыт
 * inline-ряд чайной) — через кастомную confirm-модалку (A3). Сохранение —
 * авто-сейв на коммит контрола (useBranchConfig), фидбэк — flashOk «✓».
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { DEFAULT_NIGHT_CUTOFF, DEFAULT_NIGHT_END } from "@ub/shared";
import { BranchTabs } from "../../components/grid";
import { NumPad, PullHud, TimePicker } from "../../components/shared";
import { useAppData } from "../../hooks/useAppData";
import { useAppReload, usePullToRefresh } from "../../hooks/usePullToRefresh";
import { useBranchConfig } from "../../hooks/useBranchConfig";
import { EoConfigTeas, money } from "./EoConfigTeas";
import { CfgModal } from "./EoConfigModal";
import { UniPackPick } from "./UniPackPick";
import css from "./config.module.css";

type TimeField = "hours" | "tea_cutoff" | "night_cutoff" | "night_end";

const TIMEF: Record<TimeField, { label: string; step: 1 | 5 | 15; presets?: string[] }> = {
  hours: { label: "Режим работы", step: 5 },
  tea_cutoff: { label: "Отсечка чайных", step: 5, presets: ["18:00", "19:00", "20:00"] },
  night_cutoff: { label: "Начало ночного", step: 15 },
  night_end: { label: "Конец ночного", step: 15 },
};

const Moon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M15 3a8 8 0 1 0 6 13c-6 1.5-9.5-5-6-13z" />
  </svg>
);

export function EoConfigBranches({ onDirtyChange }: { onDirtyChange: (d: boolean) => void }) {
  const app = useAppData();
  const [branchId, setBranchId] = useState("");
  const [dirty, setDirty] = useState(false);
  const [pendingBranch, setPendingBranch] = useState<string | null>(null);
  const [tf, setTf] = useState<TimeField | null>(null);
  const [npOpen, setNpOpen] = useState(false);
  const cfg = useBranchConfig(branchId);
  /* Pull-to-refresh (A43.3): все данные панели — из useAppData. */
  const reloadApp = useAppReload();
  const ptr = usePullToRefresh(reloadApp);

  /* Дефолт — первый активный филиал, как только справочник приехал. */
  useEffect(() => {
    if (!branchId) {
      const first = app.branches.find((x) => x.active);
      if (first) setBranchId(first.id);
    }
  }, [app.branches, branchId]);

  const setDirtyBoth = useCallback((d: boolean) => {
    setDirty(d);
    onDirtyChange(d);
  }, [onDirtyChange]);

  const pickBranch = (id: string) => {
    if (id === branchId) return;
    if (dirty) setPendingBranch(id);
    else setBranchId(id);
  };

  const b = cfg.branch;
  const hours = b?.hours_open && b?.hours_close ? `${b.hours_open}–${b.hours_close}` : "";
  const cutoff = b?.tea_cutoff ?? "";
  const nightStart = b?.night_cutoff || DEFAULT_NIGHT_CUTOFF;
  const nightEnd = b?.night_end || DEFAULT_NIGHT_END;
  const surcharge = b?.night_surcharge ?? 0;

  const commitTime = (f: TimeField, v: string) => {
    if (f === "hours") {
      const [o, c] = v.split("–");
      if (o && c) cfg.saveBranch({ hours_open: o, hours_close: c }, "hours");
    } else if (f === "tea_cutoff") cfg.saveBranch({ tea_cutoff: v }, f);
    else if (f === "night_cutoff") cfg.saveBranch({ night_cutoff: v }, f);
    else cfg.saveBranch({ night_end: v }, f);
  };

  const tpValue =
    tf === "hours" ? hours : tf === "tea_cutoff" ? cutoff : tf === "night_cutoff" ? nightStart : nightEnd;

  /* flash.n — React key флэшащих узлов: повторное сохранение того же поля
     ремаунтит ✓/.flash и перезапускает анимации (эталон CFG:55-56). */
  const flashOf = (key: string) => (cfg.flash?.key === key ? cfg.flash : null);

  const cell = (key: string, label: ReactNode, control: ReactNode, extra = "") => {
    const f = flashOf(key);
    return (
      <div className={`${css.cell} ${extra}`}>
        <span className={css.klbl}>{label}</span>
        <div className={css.cv}>
          {control}
          {f ? <span key={f.n} className={css.okm}>✓</span> : null}
        </div>
      </div>
    );
  };

  const editBtn = (key: string, text: string, onClick: () => void) => {
    const f = flashOf(key);
    return (
      <button
        key={f ? `f${f.n}` : "s"}
        type="button"
        className={`${css.editv} ${f ? css.flash : ""}`}
        onClick={onClick}
      >
        {text}
      </button>
    );
  };

  const tog = (key: "shifting" | "tea_before" | "mc_shift", grn: boolean, onTxt: string, offTxt: string) => {
    const on = !!b?.[key];
    const f = flashOf(key);
    return (
      <button
        type="button"
        className={`${css.ctog} ${grn ? css.grn : ""} ${on ? css.on : ""}`}
        aria-pressed={on}
        onClick={() => cfg.saveBranch(key === "shifting" ? { shifting: !on } : key === "tea_before" ? { tea_before: !on } : { mc_shift: !on }, key)}
      >
        <span className={css.ttrack} />
        <span key={f ? `f${f.n}` : "s"} className={`${css.tstate} ${f ? css.flash : ""}`}>
          {on ? onTxt : offTxt}
        </span>
      </button>
    );
  };

  /* Скелетон — только пока справочник реально грузится (без искусственных 420мс). */
  if (cfg.loading) {
    return (
      <div className={css.scroll}>
        <div className={css.skel}>
          <div className={`${css.sk} ${css.skTitle}`} />
          {[0, 1, 2, 3, 4].map((i) => <div key={i} className={`${css.sk} ${css.skRow}`} />)}
          <div className={`${css.sk} ${css.skTitle} ${css.skGap}`} />
          {[0, 1, 2].map((i) => <div key={i} className={`${css.sk} ${css.skTea}`} />)}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* data-cfg-guard: тап по чипам филиалов не коммитит открытый inline-ряд
          чайной — при dirty решает confirm-модалка (Выйти = discard). */}
      <div data-cfg-guard="">
        <BranchTabs
          branches={app.branches.filter((x) => x.active)}
          active={branchId}
          onChange={pickBranch}
          allChip={false}
          accent="role"
        />
      </div>
      <div className={css.scroll} ref={ptr.ref}>
        <PullHud pull={ptr.pull} pulling={ptr.pulling} refreshing={ptr.refreshing} />
        <section className={css.card}>
          <div className={css.secRow}>
            <h2 className={css.secT}>Конфигурация: {b?.name ?? "—"}</h2>
          </div>
          <div className={css.cfgGrid}>
            {cell("shifting", "Тип сетки", tog("shifting", false, "Со сдвигом", "Без сдвига"))}
            {cell("hours", "Режим работы", editBtn("hours", hours || "—", () => setTf("hours")))}
            {cell("tea_cutoff", "Отсечка чайных", editBtn("tea_cutoff", cutoff || "—", () => setTf("tea_cutoff")))}
            {cell("tea_before", <>Чайная до {cutoff || "—"}</>, tog("tea_before", true, "Есть", "Нет"))}
            {/* 17.09 (владелец): влияние МК на сетку — настройка филиала, как сдвиг чайной */}
            {cell("mc_shift", "Мастер-класс", tog("mc_shift", false, "Сдвигает следующий слот", "Не влияет"), css.wide)}
            {/* 28.09 (ТЗ «Юниверситет»): какой учебный пакет видят операторы филиала */}
            {cell("uni_pack", "Учебный пакет", <UniPackPick value={b?.uni_pack ?? ""} flash={flashOf("uni_pack")} onPick={(v) => cfg.saveBranch({ uni_pack: v }, "uni_pack")} />, css.wide)}
          </div>
          <div className={css.secRow}>
            <span className={css.moon}><Moon /></span>
            <h3 className={`${css.secT} ${css.cy}`}>Ночной режим</h3>
          </div>
          <div className={`${css.cfgGrid} ${css.three}`}>
            {cell("night_cutoff", "Начало", editBtn("night_cutoff", nightStart, () => setTf("night_cutoff")))}
            {cell("night_end", "Конец", editBtn("night_end", nightEnd, () => setTf("night_end")))}
            {cell("night_surcharge", "Надбавка", editBtn("night_surcharge", money(surcharge), () => setNpOpen(true)))}
          </div>
          <div className={css.helper}>
            Окно пересекает полночь: слот ночной, если t&nbsp;≥&nbsp;<b>{nightStart}</b> или
            {" "}t&nbsp;&lt;&nbsp;<b>{nightEnd}</b>. Надбавка входит в сумму до скидки.
          </div>
          {cfg.hint ? <div className={css.errHint}>{cfg.hint}</div> : null}
        </section>
        <EoConfigTeas
          branchId={branchId}
          teas={cfg.teas}
          flash={cfg.flash}
          onDirty={setDirtyBoth}
          saveTea={cfg.saveTea}
          deleteTea={cfg.deleteTea}
        />
      </div>
      <TimePicker
        open={tf !== null}
        label={tf ? TIMEF[tf].label : ""}
        mode={tf === "hours" ? "range" : "single"}
        step={tf ? TIMEF[tf].step : 5}
        presets={tf ? TIMEF[tf].presets : undefined}
        value={tpValue}
        onCommit={(v) => {
          if (tf) commitTime(tf, v);
          setTf(null);
        }}
        onCancel={() => setTf(null)}
      />
      <NumPad
        open={npOpen}
        label="Надбавка за ночной слот"
        mode="money"
        value={surcharge}
        presets={[500, 1000, 1500]}
        onCommit={(v) => cfg.saveBranch({ night_surcharge: v }, "night_surcharge")}
        onClose={() => setNpOpen(false)}
      />
      {pendingBranch ? (
        <CfgModal
          tone="warn"
          title="Несохранённая правка"
          actionLabel="Выйти"
          onAction={() => {
            setDirtyBoth(false);
            setBranchId(pendingBranch);
            setPendingBranch(null);
          }}
          onCancel={() => setPendingBranch(null)}
        >
          Есть несохранённая правка — выйти без сохранения?
        </CfgModal>
      ) : null}
    </>
  );
}
